import {
  Button,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Company, CompanyType } from '../../../../types';
import { useEffect, useState } from 'react';
import { createCompany, updateCompany } from '../../../../utils/httpRequest';

export interface CreateClientModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isEdit?: boolean;
  company?: Company;
}

const CreateClientModal = ({
  open,
  onClose,
  onConfirm,
  isEdit,
  company,
}: CreateClientModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const data = {
        ...company,
        ...form.getFieldsValue(),
      };
      await (isEdit ? updateCompany(data) : createCompany(data));
      onConfirm();
    } catch (err: any) {
      if (!err?.errorFields) {
        messageApi.error(err?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  useEffect(() => {
    form.resetFields();
    if (company) {
      form.setFieldsValue(company);
    }
  }, [company, form]);

  return (
    <Modal
      title={
        isEdit ? t('edit_client', { name: company?.name }) : t('add_new_client')
      }
      open={open}
      closable
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap={16}>
          <Button type="primary" ghost onClick={onClose} loading={loading}>
            {t('cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} loading={loading}>
            {t('confirm')}
          </Button>
        </Flex>
      }
    >
      {contextHolder}
      <Typography.Text type="secondary">
        {t('fill_company_information')}
      </Typography.Text>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ margin: 20 }}>
          <Form.Item
            label={t('company_name')}
            name="name"
            rules={[
              {
                required: true,
                message: t('required_company_name'),
              },
            ]}
          >
            <Input type="text" placeholder={t('enter')} />
          </Form.Item>
          <Form.Item
            label={t('type')}
            name="type"
            rules={[
              {
                required: true,
                message: t('required_company_type'),
              },
            ]}
          >
            <Select
              options={[
                {
                  label: CompanyType.SOLVV,
                  value: CompanyType.SOLVV,
                },
                {
                  label: CompanyType.DEMO,
                  value: CompanyType.DEMO,
                },
                {
                  label: CompanyType.REAL,
                  value: CompanyType.REAL,
                },
              ]}
              placeholder={t('select')}
            />
          </Form.Item>
          <Form.Item label={t('address')} name="address">
            <Input type="text" placeholder={t('enter')} />
          </Form.Item>
          <Form.Item label={t('contact')} name="contact">
            <Input type="text" placeholder={t('enter')} />
          </Form.Item>
          <Form.Item label={t('description')} name="description">
            <Input.TextArea placeholder={t('enter')} rows={3} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateClientModal;
